import React, { useEffect, useState } from 'react';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FiClock, FiFileText, FiLink } from 'react-icons/fi';
import { Loader } from '@/components/custom/loader';
import { DashboardContent } from '@/layouts/dashboard';
import { TiaBanner } from '@/components/custom/tia-banner';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ACTIVITY_BADGE_CLASSNAME, OUTLINE_MISSION_BUTTON_CLASSNAME, activityStudentStatusVariant } from '../../../activityStatusVariant';
import tiaAtividades from '@/assets/images/tia/TIA-ATIVIDADES.png';

export default function ComplementaryActivityEvaluationViewPlayful(props) {
  const { t } = useTranslation();
  const client = useClient();
  const [isLoading, setIsLoading] = useState(true);
  const [activity, setActivity] = useState();
  const [activityStudent, setActivityStudent] = useState();

  useEffect(() => {
    getActivity();
    getActivityStudent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getActivity = async () => {
    try {
      setIsLoading(true);
      const { data } = await client.fetch(['activities', props.activityId]);
      setActivity(data);
    } catch (e) {
      console.error(e);
      toast.error(t('toast.errorGetActivity'));
    } finally {
      setIsLoading(false);
    }
  };

  const getActivityStudent = async () => {
    try {
      const { data } = await client.fetch(['activities', props.activityId, 'activity_students', props.id]);
      setActivityStudent(data);
    } catch (e) {
      console.error(e);
      toast.error(t('toast.errorGetActivityStudent'));
    }
  };

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: '/atividade-complementar',
        name: 'Atividade Complementar',
        isActive: false
      },
      {
        route: '/atividade-complementar',
        name: 'Avaliação do professor',
        isActive: true
      }
    ]
  };

  if (isLoading || !activity || !activityStudent) {
    return (
      <DashboardContent
        variant="plain"
        breadcrumbs={breadcrumbs}
      >
        <Loader />
      </DashboardContent>
    );
  }

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <TiaBanner
        className="mb-6 sm:mt-20"
        image={tiaAtividades}
        imageOffset={210}
        text="Aqui está o retorno da sua atividade! Confira os comentários e veja como foi sua entrega. Continue assim! 🌟"
      />

      <div className="rounded-2xl border border-neutral-lightest bg-white p-6 shadow-card">
        <div className="mb-2 flex flex-wrap items-center gap-1.5">
          <Badge
            variant={activityStudentStatusVariant(activityStudent['status'], t)}
            className={ACTIVITY_BADGE_CLASSNAME}
          >
            {activityStudent['status']}
          </Badge>
        </div>

        <h1
          className="mb-1 text-xl font-extrabold"
          style={{ color: '#222731' }}
        >
          {activity.title}
        </h1>

        <div className="flex items-center gap-1.5 text-sm font-semibold text-neutral-500">
          <FiClock className="h-4 w-4 text-[var(--color-mission-medium)]" />
          {t('textsCommon.module')} {activity.modulus} | {t('textsCommon.unit')} {activity.unit} | {activity['class-hours'] < 2 ? `${activity['class-hours']} hora` : `${activity['class-hours']} ${t('tasks.hours')}`}
        </div>

        <div className="mt-6 space-y-4">
          {activityStudent.comments.map(comment => {
            const isStudent = comment.user.includes('Aluno');
            const hasStudentFile = isStudent && activityStudent['activity-file'] !== null;

            return (
              <div
                key={comment?.id}
                className={cn('rounded-2xl border p-5', isStudent ? 'border-transparent bg-bright' : 'border-neutral-lightest bg-white')}
              >
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <h3
                      className="mb-0 text-sm font-extrabold"
                      style={{ color: '#222731' }}
                    >
                      {isStudent ? t('activity.titleStudentAnswer') : t('activity.teacherEvaluation')}
                    </h3>
                    <span className="text-xs font-semibold text-neutral-500">
                      {t('activity.in')} {moment(comment['created-at']).format(t('datesForm.formatDate'))}
                    </span>
                  </div>

                  {hasStudentFile && (
                    <a
                      href={activityStudent['activity-file']?.url || activityStudent['activity-file']?.file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex shrink-0 items-center gap-1.5 text-xs font-bold text-[var(--color-mission)] hover:underline"
                    >
                      {activityStudent['activity-file']?.url ? <FiLink className="h-3.5 w-3.5" /> : <FiFileText className="h-3.5 w-3.5" />}
                      {activityStudent['activity-file']?.url ? t('activity.sendUrl') : t('activity.sendFiles')}
                    </a>
                  )}
                </div>

                <p className="mb-0 text-sm text-neutral-dark">{comment.body}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-6 rounded-2xl border border-neutral-lightest p-5">
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3
                className="mb-0 text-sm font-extrabold"
                style={{ color: '#222731' }}
              >
                {t('activity.titleActivityDescription')}
              </h3>
              <span className="text-xs font-semibold text-neutral-500">
                {t('activity.in')} {moment(activity['publish-at']).format(t('datesForm.formatDate'))}
              </span>
            </div>

            {activity['activity-file'] && (
              <a
                href={activity['activity-file']?.url || activity['activity-file']?.file}
                target="_blank"
                rel="noopener noreferrer"
                className={OUTLINE_MISSION_BUTTON_CLASSNAME}
              >
                {t('button.supportMaterial')}
              </a>
            )}
          </div>

          <p className="mb-0 text-sm text-neutral-dark">{activity.description}</p>
        </div>
      </div>
    </DashboardContent>
  );
}
